import React, { useState } from 'react';

import SizePlot from '../SizePlot';
import { SizeDetectionControls } from '../SizeDetectionControls';

export const DEFAULT_SIZE_PARAMS = {
  prominence: 0.001,
  area_threshold: 0.001,
  min_width_seconds: 0.05,
  min_distance_seconds: 1,
};

function getStatusClass(status) {
  if (status === 'running') return 'text-yellow-600';
  if (status === 'completed') return 'text-green-600';
  if (status === 'preview') return 'text-sky-600';
  return 'text-red-600';
}

function hasValue(value) {
  return value !== null && value !== undefined && value !== '';
}

function titleCase(value) {
  if (!hasValue(value)) return '';
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatNumber(value, digits = 2) {
  const parsed = value == null || value === '' ? Number.NaN : Number(value);
  if (!Number.isFinite(parsed)) return null;
  return parsed.toFixed(digits);
}

function formatBool(value) {
  if (value === true) return 'OK';
  if (value === false) return 'Review';
  return null;
}

function cleanFlagText(value) {
  if (!hasValue(value)) return null;
  return String(value)
    .replace(/^valid:\s*/i, '')
    .replace(/^ok:\s*/i, '')
    .replace(/^caution:\s*/i, '')
    .replace(/^invalid:\s*/i, '');
}

function firstFlagWord(value) {
  if (!hasValue(value)) return null;
  const match = String(value).match(/^(valid|ok|caution|invalid):/i);
  return match ? titleCase(match[1]) : null;
}

function formatCheckText({ level, flag, ok }) {
  const label = hasValue(level)
    ? titleCase(level)
    : firstFlagWord(flag) || formatBool(ok);
  const detail = cleanFlagText(flag);

  if (label && detail) {
    return `${label} — ${detail}`;
  }
  return label || detail || null;
}

function getValidityResult(component) {
  return component?.validity?.status === 'computed' && component.validity?.result
    ? component.validity.result
    : null;
}

function getPrimaryValidity(component) {
  const result = getValidityResult(component);
  if (hasValue(result?.display_validity_level)) {
    return {
      label: titleCase(result.display_validity_level),
      level: String(result.display_validity_level).toLowerCase(),
      detail: result.display_validity_flag || null,
    };
  }

  if (result) {
    const hdcLabel = formatCheckText({
      flag: result.hdc_flag,
      ok: result.hdc_ok,
    });
    return {
      label: hdcLabel || 'OK',
      level: result.hdc_ok === false ? 'caution' : 'valid',
      detail: result.hdc_flag || null,
    };
  }

  return {
    label: component?.validity?.status || 'n/a',
    level: 'unknown',
    detail: null,
  };
}

function getValidityBadgeClass(level) {
  if (level === 'valid') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  }
  if (level === 'caution') {
    return 'border-amber-200 bg-amber-50 text-amber-700';
  }
  if (level === 'invalid') {
    return 'border-rose-200 bg-rose-50 text-rose-700';
  }
  return 'border-slate-200 bg-slate-50 text-slate-600';
}

function joinPresent(parts) {
  return parts.filter(hasValue).join(' • ');
}

function buildValidityDetails(component) {
  const result = getValidityResult(component);
  if (!result) return [];

  const rows = [];

  const tauValue = joinPresent([
    hasValue(result.tau)
      ? `τ = ${formatNumber(result.tau, 3) ?? result.tau}`
      : null,
    result.tau_flag,
  ]);
  if (tauValue) {
    rows.push({ label: 'Radial mixing τ', value: tauValue });
  }

  const peValue = formatCheckText({
    level: result.pe_display_level,
    flag: result.pe_display_flag,
  });
  if (peValue) {
    rows.push({ label: 'PE', value: peValue });
  }

  const hdcValue = formatCheckText({
    flag: result.hdc_flag,
    ok: result.hdc_ok,
  });
  if (hdcValue) {
    rows.push({ label: 'HDC', value: hdcValue });
  }

  const fractionValue = joinPresent([
    hasValue(result.fraction_pct)
      ? `${formatNumber(result.fraction_pct, 2) ?? result.fraction_pct}%`
      : null,
    formatCheckText({
      level: result.fraction_display_level,
      flag: result.fraction_flag,
    }),
    hasValue(result.fraction_ok) ? formatBool(result.fraction_ok) : null,
  ]);
  if (fractionValue) {
    rows.push({ label: 'Injection fraction', value: fractionValue });
  }

  const hasT0DeviationFields = [
    result.t0_predicted,
    result.t0_deviation_sigma,
    result.t0_deviation_display_level,
    result.t0_deviation_ok,
  ].some(hasValue);
  const t0DeviationValue = joinPresent([
    hasValue(result.t0_predicted)
      ? `Predicted t0 ${formatNumber(result.t0_predicted, 2) ?? result.t0_predicted} s`
      : null,
    hasValue(result.t0_deviation_sigma)
      ? `Deviation ${formatNumber(result.t0_deviation_sigma, 2) ?? result.t0_deviation_sigma}`
      : null,
    formatCheckText({
      level: result.t0_deviation_display_level,
      flag:
        hasT0DeviationFields && result.t0_deviation_flag !== 't0 predicted not available'
          ? result.t0_deviation_flag
          : null,
    }),
    hasValue(result.t0_deviation_ok) ? formatBool(result.t0_deviation_ok) : null,
  ]);
  if (hasT0DeviationFields && t0DeviationValue) {
    rows.push({ label: 't0 deviation', value: t0DeviationValue });
  }

  if (Array.isArray(result.notes) && result.notes.length > 0) {
    const notes = result.notes.filter(hasValue);
    if (notes.length > 0) {
      rows.push({ label: 'Notes', value: notes.join(' • ') });
    }
  }

  return rows;
}

function ComponentValidityCell({ component }) {
  const primary = getPrimaryValidity(component);
  const details = buildValidityDetails(component);

  return (
    <div className="space-y-2">
      <span
        className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${getValidityBadgeClass(primary.level)}`}
        title={primary.detail || undefined}
      >
        {primary.label}
      </span>
      {details.length > 0 && (
        <details className="text-xs text-slate-600">
          <summary className="cursor-pointer font-medium text-slate-700">
            Validity details
          </summary>
          <dl className="mt-2 space-y-1">
            {details.map((item) => (
              <div key={item.label}>
                <dt className="font-semibold text-slate-700">{item.label}</dt>
                <dd className="text-slate-600">{item.value}</dd>
              </div>
            ))}
          </dl>
        </details>
      )}
    </div>
  );
}

const COMPONENT_TRACE_COLORS = [
  '#2563eb',
  '#16a34a',
  '#9333ea',
  '#dc2626',
  '#0891b2',
  '#ca8a04',
  '#4f46e5',
  '#be123c',
];

function formatR2(value) {
  const parsed = value == null || value === '' ? Number.NaN : Number(value);
  if (!Number.isFinite(parsed)) return null;
  return parsed.toFixed(3);
}

function formatTableNumber(value, digits = 2) {
  const parsed = value == null || value === '' ? Number.NaN : Number(value);
  if (!Number.isFinite(parsed)) return '—';
  return parsed.toFixed(digits);
}

function formatScientific(value, digits = 2) {
  const parsed = value == null || value === '' ? Number.NaN : Number(value);
  if (!Number.isFinite(parsed)) return '—';
  return parsed.toExponential(digits);
}

function finiteNumber(value) {
  const parsed = value == null || value === '' ? Number.NaN : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function buildTaylorgramRegions(regions) {
  if (!Array.isArray(regions)) return [];

  return regions
    .map((region, idx) => {
      const x1 = finiteNumber(region?.tmin);
      const x2 = finiteNumber(region?.tmax);
      if (x1 === null || x2 === null || x1 === x2) return null;
      const peakIndex = region?.peak_index ?? idx + 1;
      return {
        key: `region-${peakIndex}-${idx}`,
        x1: Math.min(x1, x2),
        x2: Math.max(x1, x2),
        fill: '#2563eb',
        fillOpacity: 0.08,
        stroke: '#2563eb',
        strokeOpacity: 0.18,
        label: {
          value: `Peak ${peakIndex}`,
          position: 'insideTop',
          fontSize: 10,
          fill: '#475569',
        },
      };
    })
    .filter(Boolean);
}

function buildT0Markers(values) {
  if (!Array.isArray(values)) return [];

  return values
    .map((value, idx) => {
      const x = finiteNumber(value);
      if (x === null) return null;
      return {
        key: `t0-${idx}-${x}`,
        x,
        stroke: '#334155',
        strokeDasharray: '4 3',
        label: {
          value: `t0 ${idx + 1}`,
          position: 'top',
          fontSize: 10,
          fill: '#334155',
        },
      };
    })
    .filter(Boolean);
}

function getBaselineValue(baseline, time, idx) {
  if (Array.isArray(baseline)) {
    return finiteNumber(baseline[idx]);
  }

  if (baseline && typeof baseline === 'object') {
    if (Array.isArray(baseline.values)) {
      return finiteNumber(baseline.values[idx]);
    }
    if (baseline.type === 'linear') {
      const c0 = finiteNumber(baseline.c0);
      const c1 = finiteNumber(baseline.c1);
      const tMean = finiteNumber(baseline.t_mean) ?? 0;
      if (c0 === null || c1 === null) return null;
      return c0 + c1 * (time - tMean);
    }
    if (Object.prototype.hasOwnProperty.call(baseline, 'value')) {
      return finiteNumber(baseline.value);
    }
  }

  return finiteNumber(baseline);
}

function hasRenderableBaseline(baseline, times) {
  if (baseline === null || baseline === undefined || !Array.isArray(times)) return false;
  return times.some((time, idx) => {
    const numericTime = finiteNumber(time);
    if (numericTime === null) return false;
    return getBaselineValue(baseline, numericTime, idx) !== null;
  });
}

function getRenderableBaselines(peak) {
  if (!Array.isArray(peak?.baselines)) return [];
  return peak.baselines.filter((baseline) => hasRenderableBaseline(baseline, peak.time));
}

function countBaselineTraces(peak) {
  return getRenderableBaselines(peak).length;
}

function buildPeakData(peak, showFitDetails) {
  if (!Array.isArray(peak.time) || peak.time.length === 0) return [];

  const renderableBaselines = showFitDetails ? getRenderableBaselines(peak) : [];

  return peak.time.map((time, idx) => {
    const numericTime = finiteNumber(time);
    const row = {
      time,
      signal: peak.signal?.[idx] ?? null,
      fit: peak.fit?.[idx] ?? null,
    };

    if (showFitDetails && Array.isArray(peak.components)) {
      peak.components.forEach((component, componentIdx) => {
        if (Array.isArray(component)) {
          row[`component_${componentIdx + 1}`] = component[idx] ?? null;
        }
      });
    }

    if (showFitDetails) {
      renderableBaselines.forEach((baseline, baselineIdx) => {
        if (numericTime === null) return;
        row[`baseline_${baselineIdx + 1}`] = getBaselineValue(baseline, numericTime, idx);
      });
    }

    return row;
  });
}

function buildPeakLines(peak, showFitDetails) {
  const lines = [
    { dataKey: 'signal', name: 'Signal', color: '#8884d8' },
    { dataKey: 'fit', name: 'Fit', color: '#ff7300' },
  ];

  if (!showFitDetails) return lines;

  if (Array.isArray(peak.components)) {
    peak.components.forEach((component, idx) => {
      if (!Array.isArray(component) || component.length === 0) return;
      lines.push({
        dataKey: `component_${idx + 1}`,
        name: `Gaussian component ${idx + 1}`,
        color: COMPONENT_TRACE_COLORS[idx % COMPONENT_TRACE_COLORS.length],
        strokeWidth: 1,
      });
    });
  }

  const baselines = getRenderableBaselines(peak);
  if (baselines.length > 0) {
    baselines.forEach((baseline, idx) => {
      lines.push({
        dataKey: `baseline_${idx + 1}`,
        name: `Baseline ${idx + 1}`,
        color: '#64748b',
        strokeWidth: 1,
      });
    });
  }

  return lines;
}

function PeakPlot({ peak }) {
  const [showFitDetails, setShowFitDetails] = useState(false);
  const componentTraceCount = Array.isArray(peak.components)
    ? peak.components.filter((component) => Array.isArray(component) && component.length > 0).length
    : 0;
  const hasComponentTraces = componentTraceCount > 0;
  const baselineTraceCount = countBaselineTraces(peak);
  const hasBaselineTraces = baselineTraceCount > 0;
  const hasFitDetails = hasComponentTraces || hasBaselineTraces;
  const r2 = formatR2(peak.r2);
  const data = buildPeakData(peak, showFitDetails && hasFitDetails);
  const t0Markers = buildT0Markers(peak.t0_values);
  if (!data.length) return null;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-lg font-semibold">Peak {peak.peak_index}</h3>
          {(r2 || (showFitDetails && hasFitDetails)) && (
            <div className="text-sm text-slate-600">
              {r2 && <span>R² {r2}</span>}
              {showFitDetails && hasComponentTraces && (
                <span className={r2 ? 'ml-3' : undefined}>
                  {componentTraceCount} fitted Gaussian component
                  {componentTraceCount === 1 ? '' : 's'}
                </span>
              )}
              {showFitDetails && hasBaselineTraces && (
                <span className={r2 || hasComponentTraces ? 'ml-3' : undefined}>
                  {baselineTraceCount} baseline trace
                  {baselineTraceCount === 1 ? '' : 's'}
                </span>
              )}
            </div>
          )}
        </div>
        {hasFitDetails && (
          <button
            type="button"
            className="rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
            onClick={() => setShowFitDetails((current) => !current)}
          >
            {showFitDetails ? 'Hide fit details' : 'Show fit details'}
          </button>
        )}
      </div>
      <SizePlot
        data={data}
        lines={buildPeakLines(peak, showFitDetails && hasFitDetails)}
        referenceLines={t0Markers}
      />
    </div>
  );
}

export function SizeRunView({
  sampleName = '—',
  showSampleSummary = true,
  showDetectionControls = true,
  compactResults = false,
  taylorgramReferenceLines = [],
  status = '—',
  components = [],
  messages = [],
  plots = null,
  failureMessage = null,
  params = null,
  footerNote = null,
}) {
  const detectionParams = params?.detection ?? { ...DEFAULT_SIZE_PARAMS };

  const sizeComponents = Array.isArray(components) ? components : [];
  const sizeMessages = Array.isArray(messages) ? messages : [];
  const sizePlots = plots || null;
  const taylorData = Array.isArray(sizePlots?.taylorgram?.time)
    ? sizePlots.taylorgram.time.map((time, idx) => ({
        time,
        signal: sizePlots.taylorgram.signal?.[idx] ?? null,
        smoothed: sizePlots.taylorgram.smoothed?.[idx] ?? null,
      }))
    : [];
  const taylorRegions = buildTaylorgramRegions(sizePlots?.taylorgram?.regions);
  const peakPlots = Array.isArray(sizePlots?.peaks) ? sizePlots.peaks : [];

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
      {showDetectionControls && <div className="lg:col-span-4">
        <SizeDetectionControls
          values={detectionParams}
          onChange={() => {}}
          disabled
        />
      </div>}
      {footerNote && (
        <p className="px-1 text-xs text-slate-500 lg:col-span-4">{footerNote}</p>
      )}
      {showSampleSummary && (
        <aside className="space-y-6">
          <div className="p-6 bg-white rounded-xl shadow">
            <h3 className="text-lg font-semibold mb-2">Sample</h3>
            <div className="text-xl">{sampleName || '—'}</div>
            <div className="mt-2 text-sm">
              <span className="font-medium">Status:</span>{' '}
              <span className={getStatusClass(status)}>
                {status || '—'}
              </span>
            </div>
          </div>
        </aside>
      )}

      <main
        className={`space-y-6 ${
          showSampleSummary ? 'lg:col-span-3' : 'lg:col-span-4'
        }`}
      >
        {status === 'failed' ? (
          <div className="text-red-600">
            {failureMessage || 'Analysis failed.'}
          </div>
        ) : (
          <div className="space-y-4">
            {sizeMessages.length > 0 && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded">
                {sizeMessages.map((msg, idx) => (
                  <p key={idx} className="text-sm text-yellow-800">
                    {msg}
                  </p>
                ))}
              </div>
            )}
            {sizeComponents.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">
                        Component
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">
                        Peak
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">
                        σ (s)
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">
                        t₀ (s)
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">
                        Dh (nm)
                      </th>
                      {!compactResults && (<th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">
                        Dh error
                      </th>)}
                      {!compactResults && (<th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">
                        Diffusion (m²/s)
                      </th>)}
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">
                        R²
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">
                        RMSE
                      </th>
                      {!compactResults && (<th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">
                        t₀ error
                      </th>)}
                      {!compactResults && (<th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">
                        σ error
                      </th>)}
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">
                        Validity
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {sizeComponents.map((component) => (
                      <tr key={`${component.peak_index}-${component.component_index}`}>
                        <td className="px-4 py-2 text-sm text-gray-700">
                          {component.component_index}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700">{component.peak_index}</td>
                        <td className="px-4 py-2 text-sm text-gray-700">
                          {formatTableNumber(component.sigma, 3)}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700">
                          {formatTableNumber(component.t0, 3)}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700">
                          {formatTableNumber(component.dh_nm, 2)}
                        </td>
                        {!compactResults && (<td className="px-4 py-2 text-sm text-gray-700">
                          {formatTableNumber(component.dh_error_nm, 2)}
                        </td>)}
                        {!compactResults && (<td className="px-4 py-2 text-sm text-gray-700">
                          {formatScientific(component.diffusion, 2)}
                        </td>)}
                        <td className="px-4 py-2 text-sm text-gray-700">
                          {formatR2(component.r2) || '—'}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700">
                          {compactResults ? formatScientific(component.rmse, 3) : formatTableNumber(component.rmse, 3)}
                        </td>
                        {!compactResults && (<td className="px-4 py-2 text-sm text-gray-700">
                          {formatTableNumber(component.t0_error, 3)}
                        </td>)}
                        {!compactResults && (<td className="px-4 py-2 text-sm text-gray-700">
                          {formatTableNumber(component.sigma_error, 3)}
                        </td>)}
                        <td className="px-4 py-2 text-sm text-gray-700">
                          <ComponentValidityCell component={component} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-gray-500">Waiting for results…</div>
            )}
            {taylorData.length > 0 && (
              <SizePlot
                title="Taylorgram"
                data={taylorData}
                lines={[
                  { dataKey: 'signal', name: 'Signal', color: '#8884d8' },
                  { dataKey: 'smoothed', name: 'Smoothed', color: '#82ca9d' },
                ]}
                referenceAreas={taylorRegions}
                referenceLines={taylorgramReferenceLines}
              />
            )}
            {peakPlots.map((peak, idx) => (
              <PeakPlot
                key={`peak-plot-${peak.peak_index ?? idx}`}
                peak={peak}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
